import React, {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import type { XYCoord } from "dnd-core";
import { Box, BoxProps } from "@mui/material";

interface IReorderContext {
  options: Array<string>;
  onUpdateOrder: (options: Array<string>) => void;
  dragType: string;
}

const ReorderContext = createContext<IReorderContext>({
  options: [],
  onUpdateOrder: () => {},
  dragType: "reorder-item",
});

interface RootProps {
  options: Array<any>;
  onUpdateOrder: (val: Array<any>) => void;
  children?: ReactNode;
}

// Each Root gets its own drag type so two reorderable lists mounted at the
// same time can't accept each other's dragged items.
let rootInstanceCounter = 0;

export const Root: FC<RootProps> = ({ options, onUpdateOrder, children }) => {
  const [data, setData] = useState<Array<any>>([]);
  const dragTypeRef = useRef<string>();
  if (!dragTypeRef.current) {
    rootInstanceCounter += 1;
    dragTypeRef.current = `reorder-item-${rootInstanceCounter}`;
  }

  useEffect(() => {
    setData(options);
  }, [options]);

  const contextValue = useMemo(
    () => ({ options: data, onUpdateOrder, dragType: dragTypeRef.current! }),
    [data, onUpdateOrder],
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <ReorderContext.Provider value={contextValue}>
        {children}
      </ReorderContext.Provider>
    </DndProvider>
  );
};

interface DragItem {
  index: number;
}

interface IDragTrigger extends BoxProps {
  index: number;
  containerRef: RefObject<HTMLDivElement>;
  children?: ReactNode;
}

export const Trigger: FC<IDragTrigger> = ({
  children,
  index,
  containerRef,
  ...boxProps
}) => {
  const dragRef = useRef<HTMLDivElement>(null);
  const { options, onUpdateOrder, dragType } = useContext(ReorderContext);

  const moveCard = (dragIndex: number, hoverIndex: number) => {
    const reorderedList = [...options];
    const [removed] = reorderedList.splice(dragIndex, 1);
    reorderedList.splice(hoverIndex, 0, removed);
    onUpdateOrder?.(reorderedList);
  };

  const [, drop] = useDrop<DragItem>({
    accept: dragType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!containerRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = containerRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [, drag, preview] = useDrag({
    type: dragType,
    item: () => ({ index }),
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    drop(preview(containerRef));
    drag(dragRef);
  }, [drag, drop, preview, containerRef]);

  return (
    <Box
      {...boxProps}
      ref={dragRef}
      style={{ cursor: "pointer", ...boxProps.style }}
    >
      {children}
    </Box>
  );
};
